import type { UserBody } from '@/shared/api/generated/model';

/**
 * Текущий сеанс: токен доступа и тот, кто вошёл.
 *
 * Токен лежит в обычной переменной модуля — не в localStorage и не в
 * sessionStorage. Это не забывчивость: посторонний скрипт, попавший на
 * страницу, читает хранилище браузера целиком, а переменную внутри модуля —
 * нет, и умирает она вместе со вкладкой.
 *
 * Сеанс от этого не теряется при перезагрузке: восстановить его можно из
 * cookie с токеном обновления, до которой JavaScript вообще не дотягивается.
 * Поэтому приложение при запуске делает ровно один запрос на обновление.
 */

export type SessionState =
  { status: 'unknown' } | { status: 'anonymous' } | { status: 'authenticated'; user: UserBody };

let accessToken: string | null = null;
let state: SessionState = { status: 'unknown' };

const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function getSession(): SessionState {
  return state;
}

/** Токен для заголовка Authorization. Читает только `apiFetch`. */
export function getAccessToken(): string | null {
  return accessToken;
}

export function setSession(token: string, user: UserBody): void {
  accessToken = token;
  state = { status: 'authenticated', user };
  rememberSignedIn(true);
  emit();
}

export function clearSession(): void {
  accessToken = null;
  state = { status: 'anonymous' };
  rememberSignedIn(false);
  emit();
}

/**
 * Метка «здесь входили» в localStorage — не токен, а один бит.
 *
 * Cookie с токеном обновления из JavaScript не видно, поэтому без метки
 * приложение при каждом запуске спрашивало бы сервер «а я вошёл?» — и тому,
 * кто вышел или никогда не входил, отвечало бы 401 прямо в консоль. Метка
 * сбрасывается при выходе и при отказе в обновлении. Её отсутствие ничего
 * не значит (старый браузер, чистое хранилище) — тогда спрашиваем.
 */
const SIGNED_IN_KEY = 'flowdo.signed-in';

function rememberSignedIn(value: boolean): void {
  try {
    localStorage.setItem(SIGNED_IN_KEY, value ? 'yes' : 'no');
  } catch {
    // Приватный режим или запрет хранилища — без метки просто спросим.
  }
}

/** `false` только если последнее, что здесь случилось, — выход или отказ. */
export function maySignedIn(): boolean {
  try {
    return localStorage.getItem(SIGNED_IN_KEY) !== 'no';
  } catch {
    return true;
  }
}
